import axios from 'axios';
import type { 
  CreateCaseResponse, 
  UploadUrlResponse, 
  CaseStatusResponse 
} from '../types/kyc';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/kyc',
  timeout: 30000,
});

/**
 * Crear o retomar caso KYC
 * @param userId - ID interno del caso
 * @param externalUserId - ID del usuario en quepadre-app (opcional pero necesario para postback)
 */
export async function createCase(userId: string, externalUserId?: string): Promise<CreateCaseResponse> {
  const response = await api.post<CreateCaseResponse>('/case', { userId, externalUserId });
  return response.data;
}

/**
 * Registrar consentimiento
 */
export async function recordConsent(caseId: string): Promise<void> {
  await api.post('/consent', { caseId });
}

/**
 * Obtener URL pre-firmada para subir imagen
 */
export async function getUploadUrl(
  caseId: string,
  type: 'doc_front' | 'doc_back' | 'selfie'
): Promise<UploadUrlResponse> {
  const response = await api.post<UploadUrlResponse>('/upload-url', {
    caseId,
    type,
    contentType: 'image/jpeg',
  });
  return response.data;
}

/**
 * Subir imagen a S3 usando URL pre-firmada
 */
export async function uploadImage(
  uploadUrl: string,
  imageBlob: Blob
): Promise<void> {
  await axios.put(uploadUrl, imageBlob, {
    headers: {
      'Content-Type': 'image/jpeg',
    },
  });
}

/**
 * Crear sesión de liveness
 */
export async function createLivenessSession(caseId: string): Promise<string> {
  const response = await api.post<{ sessionId: string }>('/liveness-session', { caseId });
  return response.data.sessionId;
}

/**
 * Enviar caso para verificación
 */
export async function submitCase(
  caseId: string,
  livenessSessionId?: string
): Promise<{ status: string; message: string }> {
  const response = await api.post('/submit', { caseId, livenessSessionId });
  return response.data;
}

/**
 * Obtener estado del caso
 */
export async function getCaseStatus(caseId: string): Promise<CaseStatusResponse> {
  const response = await api.get<CaseStatusResponse>(`/status/${caseId}`);
  return response.data;
}

/**
 * Crear nuevo intento (retry)
 */
export async function createRetryAttempt(caseId: string): Promise<{ attemptId: string }> {
  const response = await api.post<{ attemptId: string }>('/retry', { caseId });
  return response.data;
}

export default api;


